<template>
  <v-dialog v-model="signupDialog" width="800">
    <template #activator="{ on }">
      <v-btn v-on="on" color="success" outlined>
        <v-icon left>fas fa-plus-circle</v-icon>
        Add Slots
      </v-btn>
    </template>
    <v-card>
      <v-card-title>Add Slots</v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <date-picker v-model="signupStartDate" label="Start Date" outlined />
          </v-col>
          <v-col>
            <date-picker v-model="signupEndDate" label="End Date" outlined />
          </v-col>
        </v-row>
        <template v-if="overlapCount > 0">
          <p>There {{ overlapCount === 1 ? 'is a sign-up slot' : `are ${overlapCount} sign-up slots` }} that already exist{{ overlapCount === 1 ? '' : 's' }} between the dates selected. Depending on what days you're adding slots for below, there could be matching overlapping days. What should be done with these overlapping days?</p>
          <v-select v-model="overlapAction" :items="overlapActionOptions" label="Action to take on overlapping dates" outlined />
        </template>
        <v-expansion-panels>
          <signup-day v-for="{ label } of signupSlots" :key="label" :label="label" @update="(slots) => slotUpdate(label, slots)" />
        </v-expansion-panels>
      </v-card-text>
      <v-alert v-if="addCompleted" type="info" outlined>Done adding sign-up slots. {{ signupsAdded > 0 && signupsUpdated > 0 ? `Added ${signupsAdded} and updated ${signupsUpdated} sign-up days.` : (signupsAdded > 0 ? `Added ${signupsAdded} sign-up days.` : `Updated ${signupsUpdated} sign-up days.`) }}</v-alert>
      <v-card-text v-if="isRunning">
        <v-alert type="info" outlined>Adding sign-up slots. Have added {{ signupsAdded }} <span v-if="overlapCount > 0">and updated {{ signupsUpdated }}</span> sign-up days so far...</v-alert>
      </v-card-text>
      <v-card-actions v-else>
        <v-btn :disabled="signupStartDate === '' || signupEndDate === '' || (overlapCount > 0 && !overlapAction)" color="success" text @click="addSlots">
          <v-icon left small>fas fa-plus-circle</v-icon>
          Add Signups
        </v-btn>
        <v-spacer />
        <v-btn text @click="signupDialog = false">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { computed, ref, watch } from '@vue/composition-api'

export default {
  components: {
    DatePicker: () => import('@/components/forms/inputs/DatePicker'),
    SignupDay: () => import('./SignupDay')
  },
  props: {
    area: String,
    term: String
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const signupDialog = ref(false)
    const signupStartDate = ref('')
    const signupEndDate = ref('')
    const signupSlots = ref([
      { label: 'Sunday', slots: [] },
      { label: 'Monday', slots: [] },
      { label: 'Tuesday', slots: [] },
      { label: 'Wednesday', slots: [] },
      { label: 'Thursday', slots: [] },
      { label: 'Friday', slots: [] },
      { label: 'Saturday', slots: [] }
    ])
    const overlapCount = ref(0)
    const overlapAction = ref('')
    const overlapActionOptions = ref([
      { text: 'Ignore days that already exist', value: 'ignore' },
      { text: 'Add slots to what is on that date', value: 'add' },
      { text: 'Replace the slots on that day with these slots', value: 'replace' }
    ])
    watch([signupStartDate, signupEndDate], async () => {
      if (signupStartDate.value && signupEndDate.value) {
        const { total } = await root.$feathers.service('serve/event').find({ query: { area: props.area, term: props.term, date: { $gte: signupStartDate.value, $lte: signupEndDate.value }, $limit: 0 } })
        overlapCount.value = total
      }
    })

    function slotUpdate (label, slots) {
      console.log('slots updated', label, slots)
      const index = signupSlots.value.findIndex(({ label: l }) => label === l)
      signupSlots.value.splice(index, 1, { label, slots })
    }
    const isRunning = ref(false)
    const signupsAdded = ref(0)
    const signupsUpdated = ref(0)
    const addCompleted = ref(false)
    async function addSlots () {
      addCompleted.value = false
      isRunning.value = true
      signupsAdded.value = 0
      signupsUpdated.value = 0
      // We'll create a separate event for each day, with the slots added based on the day of the week (if there are no slots then we won't create an event for that day)
      const dt = new Date(signupStartDate.value + ' 8:00')
      const endDt = new Date(signupEndDate.value + ' 8:00')
      while (dt.toLocaleDateString('en-CA') <= endDt.toLocaleDateString('en-CA')) {
        const dow = dt.getDay()
        if (signupSlots.value[dow].slots.length > 0) {
          const blocks = signupSlots.value[dow].slots.map(({ text, slots, hours }) => { return { desc: text, slotCount: slots, minHours: hours, maxHours: hours } })
          // Check and see if this day already exists. If it does, just add the blocks to the event's blocks
          const { data } = await root.$feathers.service('serve/event').find({ query: { area: props.area, term: props.term, date: dt.toLocaleDateString('en-CA') } })
          if (data.length > 0) {
            switch (overlapAction.value) {
              case 'ignore':
                continue
              case 'add':
                const { blocks: updBlock } = data[0]
                blocks.forEach((row) => { updBlock.push(row) })
                await root.$feathers.service('serve/event').patch(data[0]._id, { blocks: updBlock })
                signupsUpdated.value++
                break
              case 'replace':
                await root.$feathers.service('serve/event').patch(data[0]._id, { blocks })
                signupsUpdated.value++
                break
            }
          } else {
            const dateString = dt.toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
            const obj = {
              name: props.area + ' Sign-Up Slots',
              date: dateString,
              active: true,
              area: props.area,
              term: props.term,
              user: user.value.username,
              regOpen: new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
              regClosed: dateString,
              blocks
            }
            await root.$feathers.service('serve/event').create(obj)
            signupsAdded.value++
          }
        }
        dt.setDate(dt.getDate() + 1)
      }
      isRunning.value = false
      addCompleted.value = true
    }

    return {
      signupDialog,
      signupStartDate,
      signupEndDate,
      signupSlots,
      overlapCount,
      overlapAction,
      overlapActionOptions,
      slotUpdate,
      isRunning,
      signupsAdded,
      signupsUpdated,
      addCompleted,
      addSlots
    }
  }
}
</script>
